import { DateTime } from 'luxon'
import * as Components from './components'
import { ComponentProps } from 'react'
import { z } from 'zod/v4'

const ALPHA_WHITESPACE_DASH_REGEX = /^[a-zA-Z\s-]+$/

/**
 * @see {@link https://highnote.com/docs/reference/input_object/AddressInput}
 * When the field is used for creating an application, it will also validate that a PO Box is not included using this regex pattern
 */
const CARD_APPLICATION_STREET_1_PRIMARY_PATTERN =
  /^(?:((\S([^pPOo])+)|(?:[0-9]+)))\s(?:[0-9A-Za-z.]|[^\S\r\n])+$/

const CARD_APPLICATION_STREET_2_PATTERN = /^[a-zA-Z\d',. \-#]+(([',. \-#][a-zA-Z ])?[a-zA-Z.]*)*$/

/**
 * @see {@link https://highnote.com/docs/reference/input_object/AddressInput}
 * When the field is used for creating a physical payment card order or physical payment card group order, it will also support mailing to a PO Box using this regex pattern
 */
const CARD_APPLICATION_STREET_1_MAILING_PATTERN =
  /^(?:(?:(?:\S[^pPOo\r\n]+)|(?:[0-9/#]+)))\s(?:[0-9A-Za-z.]|[^\S\r\n])+$|^\b[P|p]*(?:OST|ost)*\.*\s*[O|o|0]*(?:ffice|FFICE)*\.*\s*[B|b][O|o|0][X|x]\s[0-9A-Za-z]+\b$/

export const CardApplicationFormSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First Name is required')
    .max(50, 'First Name must be less than 50 characters')
    .regex(ALPHA_WHITESPACE_DASH_REGEX, 'First Name must only contain alphabetical characters'),
  lastName: z
    .string()
    .min(1, 'Last Name is required')
    .max(50, 'Last Name must be less than 50 characters')
    .regex(ALPHA_WHITESPACE_DASH_REGEX, 'Last Name must only contain alphabetical characters'),
  phoneNumber: z
    .string()
    .min(1, 'Phone Number is required')
    .regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone Number is not valid.'),
  email: z.email('Please enter a valid email address.'),
  offerCode: z.string().optional(),
  primaryAddress: z.object({
    street1: z
      .string()
      .regex(
        CARD_APPLICATION_STREET_1_PRIMARY_PATTERN,
        'Street 1 is not a valid address or cannot be a PO Box',
      ),
    street2: z
      .string()
      .regex(CARD_APPLICATION_STREET_2_PATTERN, 'Street 2 does not match the required pattern')
      .or(z.literal(''))
      .optional(),
    city: z.string().regex(/^['\p{L}]+(?:[ \p{L},'-:])*$/u, 'City is not valid'),
    state: z.string().regex(/^[A-Z]{2}$/, 'State must be a 2 letter abbreviation'),
    zipCode: z
      .string()
      .regex(
        /^\d{5}(-\d{4})?$/,
        'Zip Code must be either 5 digits or 5 digits followed by a hyphen and 4 digits',
      ),
  }),
  isMailingAddressDifferent: z.boolean(),
  mailingAddress: z
    .object({
      street1: z
        .string()
        .regex(
          CARD_APPLICATION_STREET_1_MAILING_PATTERN,
          'Street 1 is not a valid address or cannot be a PO Box',
        ),
      street2: z
        .string()
        .regex(CARD_APPLICATION_STREET_2_PATTERN, 'Street 2 does not match the required pattern')
        .or(z.literal(''))
        .optional(),
      city: z.string().regex(/^['\p{L}]+(?:[ \p{L},'-:])*$/u, 'City is not valid'),
      state: z.string().regex(/^[A-Z]{2}$/, 'State must be a 2 letter abbreviation'),
      zipCode: z
        .string()
        .regex(
          /^\d{5}(-\d{4})?$/,
          'Zip Code must be either 5 digits or 5 digits followed by a hyphen and 4 digits',
        ),
    })
    .optional(),
  ssn: z.string().regex(/^\d{3}-\d{2}-\d{4}$/, 'SSN must be in the format ###-##-####'),
  dob: z
    .date()
    .refine((date) => date <= DateTime.now().minus({ years: 18 }).toJSDate(), {
      message: 'You must be 18 years or older to apply for a card.',
    })
    .refine((date) => date.getFullYear() >= 1900, {
      message: 'Year of birth must be after 1900',
    })
    .optional(),
  totalAnnualIncome: z.string().regex(/^\d+$/, { message: 'Total Annual Income must be a number' }),
  monthlyHousingPayment: z
    .string()
    .regex(/^\d+$/, { message: 'Monthly Housing Payment must be a number' }),
  acceptCardholderAgreement: z.boolean().refine((value) => value, {
    message: 'You must accept the cardholder agreement to proceed.',
  }),
})

export type CardApplicationForm = z.infer<typeof CardApplicationFormSchema>

export const FormInputProps = {
  firstName: {
    autoComplete: 'given-name',
    autoCapitalize: 'words',
    placeholder: 'First name',
    sardineId: 'firstName',
  },
  lastName: {
    autoComplete: 'family-name',
    autoCapitalize: 'words',
    placeholder: 'Last name',
    sardineId: 'lastName',
  },
  phoneNumber: {
    autoComplete: 'tel',
    autoCapitalize: 'none',
    placeholder: 'Phone number',
    readOnly: true,
    disabled: true,
  },
  email: {
    autoComplete: 'email',
    autoCapitalize: 'none',
    placeholder: 'Email',
    readOnly: true,
    disabled: true,
  },
  offerCode: {
    autoComplete: 'off',
    autoCapitalize: 'none',
    placeholder: 'Promo Code',
    sardineId: 'offerCode',
  },
  isMailingAddressDifferent: {},
  dob: {
    autoComplete: 'birthdate-full',
    autoCapitalize: 'none',
    placeholder: 'Date of birth',
    sardineId: 'dob',
  },
  ssn: {
    autoComplete: 'off',
    placeholder: 'Social security number',
    sardineId: 'ssn',
  },
  totalAnnualIncome: {
    autoComplete: 'off',
    placeholder: 'Total annual income',
    sardineId: 'totalAnnualIncome',
  },
  monthlyHousingPayment: {
    autoComplete: 'off',
    placeholder: 'Monthly housing payment',
    sardineId: 'monthlyHousingPayment',
  },
} as const satisfies Record<
  keyof Omit<
    CardApplicationForm,
    'primaryAddress' | 'mailingAddress' | 'acceptCardholderAgreement'
  >,
  Partial<ComponentProps<typeof Components.FormInput>>
>
